import { zodResolver } from '@hookform/resolvers/zod'
import type { FC } from 'react'
import { useId } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { TextArea } from '@/components/commons/textarea'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle
} from '@/components/ui/modal-atoms'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useAddPhotosetSpaceComment } from '@/features/photoset/queries/use-add-photoset-space-comment'
import type { PhotosetSpace } from '@/features/photoset/types'
import { type TKey, useTranslation } from '@/translations/i18n'

const RetouchinQaCommentModalSchema = z.object({
	comment: z.string()
})
type RetouchinQaCommentModalSchema = z.infer<
	typeof RetouchinQaCommentModalSchema
>

interface RetouchingQaCommentModalProps {
	onClose: () => void
	spaceName: string
	photosetSpaceId: PhotosetSpace['id']
	comment?: string
}

export const RetouchingQaCommentModal: FC<RetouchingQaCommentModalProps> = ({
	onClose,
	spaceName,
	photosetSpaceId,
	comment
}) => {
	const { t } = useTranslation()
	const formId = useId()
	const { mutate: addPhotosetSpaceComment, isPending } =
		useAddPhotosetSpaceComment(
			{
				photosetSpaceId
			},
			{
				onSuccess: onClose
			}
		)

	const form = useForm({
		mode: 'all',
		resolver: zodResolver(RetouchinQaCommentModalSchema),
		defaultValues: { comment } as RetouchinQaCommentModalSchema
	})

	const handleSubmit = form.handleSubmit(({ comment }) => {
		addPhotosetSpaceComment({ comment: comment ?? null })
	})

	return (
		<Modal closeIconOffset='sm' onOverlayClick={onClose} width={480}>
			<ModalHeader>
				<ButtonLegacy
					onClick={onClose}
					shape='square'
					size='sm'
					type='button'
					variant='tertiary'
				>
					<XCloseIcon size={20} />
				</ButtonLegacy>
				<h3>{t('common:comment')}</h3>
			</ModalHeader>

			<Separator orientation='horizontal' />

			<ModalBody>
				<div className='flex flex-col gap-6'>
					<ModalTitle className='text-neutral-900'>
						{spaceName}
					</ModalTitle>
					<form
						className='w-full'
						id={formId}
						onSubmit={handleSubmit}
					>
						<TextArea
							errorMessage={
								form.formState.errors?.comment?.message &&
								t(form.formState.errors.comment.message as TKey)
							}
							maxLength={250}
							placeholder={t(
								'photoset:retouching_qa.comment_placeholder'
							)}
							rows={6}
							{...form.register('comment')}
						/>
					</form>
				</div>
			</ModalBody>

			<ModalFooter>
				<ButtonLegacy
					disabled={isPending}
					onClick={onClose}
					type='button'
					variant='tertiary'
				>
					{t('common:cancel')}
				</ButtonLegacy>
				<ButtonBrandLegacy
					disabled={isPending || !form.formState.isDirty}
					form={formId}
					type='submit'
				>
					{t('common:confirm')}
					{isPending && <Spinner size={16} />}
				</ButtonBrandLegacy>
			</ModalFooter>
		</Modal>
	)
}

RetouchingQaCommentModal.displayName = 'RetouchingQaCommentModal'
